import { captureException } from '@sentry/react';
import { Component, ErrorInfo, ReactNode } from 'react';

import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import posthog from '@/config/analytics/posthog';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  hasError: boolean;
}

/**
 * ErrorBoundary catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 *
 * NOTE: Error boundaries must be class components in current React versions.
 */
class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    error: null,
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { error, hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    posthog.capture(ANALYTICS_EVENTS.ERROR_BOUNDARY_TRIGGERED, {
      boundary: 'root',
      error: error.message,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-viewport bg-back-bg2 flex flex-col items-center justify-center px-4 text-center">
          <div className="border-border-subtle bg-card w-full max-w-md rounded-lg border p-8 shadow-md">
            <h2 className="text-secondary mb-4 text-2xl font-bold">Something went wrong</h2>
            <p className="text-tertiary mb-6">
              We encountered an unexpected error. Please try reloading the add-in.
            </p>
            {this.state.error && (
              <div className="bg-destructive-bg mb-6 max-h-32 overflow-auto rounded p-3 text-left">
                <code className="text-destructive text-xs">{this.state.error.toString()}</code>
              </div>
            )}
            <button
              className="bg-primary-active text-primary-on-button hover:bg-primary-hover w-full rounded-md px-4 py-2 font-medium transition-colors"
              onClick={this.handleReload}
            >
              Reload Add-in
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
